import React from "react"
import { useTranslation } from "react-i18next"
import List from "../../../components/List/List"
import { AdminFinancialMetrics } from "../../../Objects/AdminDashboard/AdminFinancialMetrics"
import { AdminStoreFinancialMetric } from "../../../Objects/AdminDashboard/AdminStoreFinancialMetric"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { AdminListCard, AdminListMetricItem } from "../components/AdminListCard"

interface StoreFinancialHealthSectionProps {
  financialMetrics: AdminFinancialMetrics
  onRefresh: () => void
}

const STORE_HEALTH_FILTERS = [
  "admin_dashboard.store_financial_health.filter_with_movement",
  "admin_dashboard.store_financial_health.filter_without_movement",
  "admin_dashboard.store_financial_health.filter_all",
] as const

const hasFinancialMovement = (item: AdminStoreFinancialMetric) =>
  item.totalOrdersCurrentMonth > 0 ||
  item.manualExternalOrdersCurrentMonth > 0 ||
  item.grossOrdersAmountCurrentMonth > 0 ||
  item.manualExternalOrdersNetAmountCurrentMonth > 0 ||
  item.splitAmountCurrentMonth > 0

const resolveHealthTone = (item: AdminStoreFinancialMetric) => {
  if (!hasFinancialMovement(item)) {
    return {
      cardClass: "rounded-xl border-2 border-slate-200 bg-slate-50 p-3",
      badgeClass: "bg-slate-100 text-slate-700",
      labelKey: "admin_dashboard.store_financial_health.status_without_movement",
    }
  }

  if (item.paidOrdersCurrentMonth <= 0) {
    return {
      cardClass: "rounded-xl border-2 border-amber-300 bg-amber-50 p-3",
      badgeClass: "bg-amber-100 text-amber-700",
      labelKey: "admin_dashboard.store_financial_health.status_attention",
    }
  }

  return {
    cardClass: "rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3",
    badgeClass: "bg-emerald-100 text-emerald-700",
    labelKey: "admin_dashboard.store_financial_health.status_with_movement",
  }
}

export const StoreFinancialHealthSection: React.FC<StoreFinancialHealthSectionProps> = ({
  financialMetrics,
  onRefresh,
}) => {
  const { t } = useTranslation("frontend")

  const dropdownFilters = STORE_HEALTH_FILTERS.map((key) => t(key))

  return (
    <div className="w-full overflow-auto bg-white p-1 md:p-0">
      <div className="p-2">
        <List
          messageCount={t("admin_dashboard.store_financial_health.count")}
          messageEmpty={t("admin_dashboard.store_financial_health.empty")}
          nameFindSearch={t("admin_dashboard.store_financial_health.search")}
          nameDropdown={dropdownFilters}
          data={financialMetrics.topStores.map((item, index) => {
            const tone = resolveHealthTone(item)
            const statusLabel = t(tone.labelKey)
            const movementLabel = hasFinancialMovement(item)
              ? t("admin_dashboard.store_financial_health.filter_with_movement")
              : t("admin_dashboard.store_financial_health.filter_without_movement")

            return {
              id: item.companyId,
              index,
              title: item.companyName,
              linkImage: "",
              isDisable: !hasFinancialMovement(item),
              dataFilter: [
                item.companyName,
                statusLabel,
                movementLabel,
                String(item.totalOrdersCurrentMonth),
                String(item.paidOrdersCurrentMonth),
                String(item.manualExternalOrdersCurrentMonth),
                convertToLocalMoney(item.grossOrdersAmountCurrentMonth),
                convertToLocalMoney(item.estimatedStoreEarningsCurrentMonth),
              ],
              children: (
                <AdminListCard
                  title={item.companyName}
                  subtitle={t("admin_dashboard.store_financial_health.current_month")}
                  badge={statusLabel}
                  badgeClass={tone.badgeClass}
                  cardClass={tone.cardClass}
                >
                  <div className="grid grid-cols-2 gap-2">
                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.orders")}
                      value={item.totalOrdersCurrentMonth}
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.finished")}
                      value={item.finishedOrdersCurrentMonth}
                      accent="text-emerald-700"
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.paid")}
                      value={item.paidOrdersCurrentMonth}
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.cancelled")}
                      value={item.cancelledOrdersCurrentMonth}
                      accent="text-red-700"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.gross_amount")}
                      value={convertToLocalMoney(item.grossOrdersAmountCurrentMonth)}
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.manual_external_net")}
                      value={convertToLocalMoney(item.manualExternalOrdersNetAmountCurrentMonth)}
                      accent="text-orange-700"
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.store_earnings")}
                      value={convertToLocalMoney(item.estimatedStoreEarningsCurrentMonth)}
                      accent="text-emerald-700"
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.average_earnings")}
                      value={convertToLocalMoney(item.averageEarningsPerPaidOrderCurrentMonth)}
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.platform_fee")}
                      value={convertToLocalMoney(item.platformFeeAmountCurrentMonth)}
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.store_financial_health.courier_amount")}
                      value={convertToLocalMoney(item.courierAmountCurrentMonth)}
                    />
                  </div>

                  <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-xs text-slate-700">
                    <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
                      <p>
                        <strong>{t("admin_dashboard.store_financial_health.manual_external_orders")}:</strong>{" "}
                        {item.manualExternalOrdersCurrentMonth}
                      </p>

                      <p>
                        <strong>{t("admin_dashboard.store_financial_health.split_amount")}:</strong>{" "}
                        {convertToLocalMoney(item.splitAmountCurrentMonth)}
                      </p>

                      <p>
                        <strong>{t("admin_dashboard.store_financial_health.store_amount")}:</strong>{" "}
                        {convertToLocalMoney(item.storeAmountCurrentMonth)}
                      </p>
                    </div>
                  </div>
                </AdminListCard>
              ),
            }
          })}
          handleRefreshButton={onRefresh}
          handleOnClick={() => {}}
        />
      </div>
    </div>
  )
}